import { toLocalDateInputValue } from './commercialDateTime';
import { formatCurrency } from './commercialFormatters';

const moneyMinorUnits = value => {
  const normalized = String(value == null ? '' : value).trim();
  if (!/^\d+(?:\.\d{1,2})?$/.test(normalized)) return Number.NaN;
  const [whole, decimal = ''] = normalized.split('.');
  return Number(whole) * 100 + Number(decimal.padEnd(2, '0'));
};

export const paymentProgress = (installments, totalAmountMinor = null) => {
  const activeInstallments = installments.filter(row => row.status !== 'CANCELLED');
  const expectedMinor = activeInstallments.reduce((sum, row) => sum + Number(row.amountMinor || 0), 0);
  const paidMinor = activeInstallments.reduce((sum, row) => sum + Number(row.paidAmountMinor || 0), 0);
  const progressTargetMinor = Number(totalAmountMinor) > 0 ? Number(totalAmountMinor) : expectedMinor;
  return {
    expectedMinor,
    paidMinor,
    remainingMinor: Math.max(0, progressTargetMinor - paidMinor),
    percentage: progressTargetMinor ? Math.min(100, Math.round((paidMinor / progressTargetMinor) * 100)) : 0,
  };
};

export const agreementNextAction = (commitment, asOf = toLocalDateInputValue()) => {
  if (!commitment) return { id: 'PLAN_TERMS', label: 'Plan terms', disabled: false, requiresConfirmation: false };
  if (commitment.status === 'CANCELLED') return { id: 'CANCELLED', label: 'Cancelled', disabled: true, requiresConfirmation: false };
  if (commitment.recognizedAt) return { id: 'COMPLETED', label: 'MRR applied', disabled: true, requiresConfirmation: false };
  if (!commitment.recognitionDate) return { id: 'PLAN_TERMS', label: 'Plan terms', disabled: false, requiresConfirmation: false };
  if (String(commitment.recognitionDate).slice(0, 10) > asOf) return { id: 'SCHEDULED', label: `Apply from ${String(commitment.recognitionDate).slice(0, 10)}`, disabled: true, requiresConfirmation: false };
  return { id: 'APPLY_TO_MRR', label: 'Apply to MRR', disabled: false, requiresConfirmation: true };
};

export const commitmentActionLabel = commitment => agreementNextAction(commitment).label;

export const agreementLifecycle = commitment => {
  const current = agreementNextAction(commitment).id;
  const currentIndex = ['PLAN_TERMS', 'APPLY_TO_MRR', 'COMPLETED'].indexOf(current);
  return [
    { id: 'PLAN_TERMS', label: 'Terms planned' },
    { id: 'APPLY_TO_MRR', label: 'Apply to MRR' },
    { id: 'COMPLETED', label: 'MRR applied' },
  ].map((step, index) => ({
    ...step,
    state: index < currentIndex || current === 'COMPLETED' ? 'complete' : index === currentIndex ? 'current' : 'upcoming',
  }));
};

export const agreementLifecycleStatus = commitment => {
  const action = agreementNextAction(commitment);
  if (action.id === 'CANCELLED') return { label: 'Cancelled', detail: 'No further action' };
  if (action.id === 'SCHEDULED') return { label: 'MRR scheduled', detail: action.label };
  if (action.id === 'PLAN_TERMS') return { label: 'Terms needed', detail: 'Recognition not planned' };
  if (action.id === 'APPLY_TO_MRR') return { label: 'MRR pending', detail: 'Ready to apply' };
  return { label: 'MRR applied', detail: 'Revenue recognition complete' };
};

export const nextOpenAgreement = (commitments, asOf = toLocalDateInputValue()) => {
  const open = (commitments || []).filter(commitment => (
    commitment.status !== 'CANCELLED' && !commitment.recognizedAt
  ));
  return open.find(commitment => !agreementNextAction(commitment, asOf).disabled)
    || open[0]
    || null;
};

export const agreementCanCorrect = commitment => (
  Boolean(commitment) && commitment.status !== 'CANCELLED' && !commitment.recognizedAt
);

export const agreementHasActiveInstallments = commitment => (
  (commitment?.installments || []).some(installment => installment.status !== 'CANCELLED')
);

export const updateAgreementKind = (form, kind, renewalId) => ({
  ...form,
  kind,
  renewalId: kind === 'RENEWAL' ? renewalId : null,
});

export const paymentPlanDifference = form => {
  const contractMinor = moneyMinorUnits(form.totalAmount);
  const stagedMinor = (form.installments || []).reduce((sum, item) => sum + moneyMinorUnits(item.amount), 0);
  return (contractMinor - stagedMinor) / 100;
};

export const agreementStepError = (form, step) => {
  if (step === 0) {
    if (!form.serviceStartsAt) return 'Add the service start date to continue.';
    if (form.contractAlignment === 'FISCAL_YEAR' && !form.fiscalYearStartMonth) return 'Choose the fiscal year start month to continue.';
    return null;
  }
  if (step === 1) {
    if (!form.recognitionDate) return 'Add the MRR recognition date to continue.';
    if (Number(form.totalAmount || 0) <= 0) return 'Contract value must be greater than zero.';
    if (!Number.isFinite(moneyMinorUnits(form.totalAmount))) return 'Contract value must use no more than two decimal places.';
    const currency = form.currency === undefined || form.currency === null
      ? 'EUR'
      : String(form.currency).trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(currency)) return 'Contract currency must be a three-letter code.';
    if ([form.totalAmount, form.previousMrr, form.resultingMrr].some(value => Number(value || 0) < 0)) return 'Contract value and resulting MRR cannot be negative.';
    return null;
  }
  const installments = form.installments || [];
  if (installments.some(item => !item.dueDate || Number(item.amount) <= 0)) return 'Every payment stage needs a due date and positive amount.';
  if (installments.some(item => !Number.isFinite(moneyMinorUnits(item.amount)))) return 'Payment-stage amounts must use no more than two decimal places.';
  if (!installments.length) return null;
  const difference = paymentPlanDifference(form);
  if (difference === 0) return null;
  return difference > 0
    ? `Payment plan is ${formatCurrency(difference, form.currency)} short of the contract value.`
    : `Payment plan exceeds the contract value by ${formatCurrency(Math.abs(difference), form.currency)}.`;
};
